import { createTheme, ThemeProvider } from '@mui/material/styles';
import type { FallbackRender } from '@sentry/react';
import { ErrorBoundary } from '@sentry/react';
import { PropsWithChildren, useLayoutEffect, useMemo } from 'react';

import { AuthProvider } from '~/modules/Auth';
import { GraphicSetting, useSettingValue, VisualThemeSetting } from '~/modules/Settings/sharedSettings';

import { GenericErrorFallback } from './GenericErrorFallback';

interface AppProvidersProps extends PropsWithChildren {
  errorFallback?: FallbackRender;
}

function ThemeRuntime({ children }: PropsWithChildren) {
  const [graphicSetting] = useSettingValue(GraphicSetting);
  const [visualTheme] = useSettingValue(VisualThemeSetting);

  useLayoutEffect(() => {
    document.documentElement.dataset.walkyTheme = visualTheme;
    document.documentElement.style.colorScheme = visualTheme;
  }, [visualTheme]);

  const theme = useMemo(
    () =>
      createTheme({
        graphicSetting,
        palette: {
          mode: visualTheme,
          primary: { main: '#f45a0a' },
          background: {
            default: visualTheme === 'dark' ? '#0f1827' : '#f6f6f7',
            paper: visualTheme === 'dark' ? '#172438' : '#ffffff',
          },
          text: {
            primary: visualTheme === 'dark' ? '#f4f7fb' : '#1f1f21',
            secondary: visualTheme === 'dark' ? '#aeb9ca' : '#6f7075',
          },
        },
      }),
    [graphicSetting, visualTheme],
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default function AppProviders({ children, errorFallback = GenericErrorFallback }: AppProvidersProps) {
  return (
    <ThemeRuntime>
      <ErrorBoundary fallback={errorFallback}>
        <AuthProvider>{children}</AuthProvider>
      </ErrorBoundary>
    </ThemeRuntime>
  );
}
